import { supabase } from '../lib/supabase';

export interface ProfileImage {
  id: string;
  user_id: string;
  profile_picture_url?: string;
  banner_image_url?: string;
  created_at: string;
  updated_at: string;
}

export class ProfileImageService {
  /**
   * Upload profile picture to Supabase Storage
   */
  static async uploadProfilePicture(
    file: File,
    userId: string
  ): Promise<{ url: string; path: string }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `profile-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  }

  /**
   * Upload banner image to Supabase Storage
   */
  static async uploadBannerImage(
    file: File,
    userId: string
  ): Promise<{ url: string; path: string }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `banner-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('profile-banners')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-banners')
        .getPublicUrl(filePath);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      console.error('Error uploading banner image:', error);
      throw error;
    }
  }

  /**
   * Get user's profile images
   */
  static async getUserProfileImages(userId: string): Promise<ProfileImage | null> {
    try {
      const { data, error } = await supabase
        .from('profile_images')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile images:', error);
      throw error;
    }
  }

  /**
   * Save or update profile images
   */
  static async saveProfileImages(
    userId: string,
    profilePictureUrl?: string,
    bannerImageUrl?: string
  ): Promise<ProfileImage> {
    try {
      // Check if profile images already exist
      const existing = await this.getUserProfileImages(userId);

      if (existing) {
        // Update existing record
        const { data, error } = await supabase
          .from('profile_images')
          .update({
            profile_picture_url: profilePictureUrl || existing.profile_picture_url,
            banner_image_url: bannerImageUrl || existing.banner_image_url,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new record
        const { data, error } = await supabase
          .from('profile_images')
          .insert({
            user_id: userId,
            profile_picture_url: profilePictureUrl,
            banner_image_url: bannerImageUrl
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error saving profile images:', error);
      throw error;
    }
  }

  /**
   * Delete profile picture
   */
  static async deleteProfilePicture(userId: string): Promise<void> {
    try {
      const profileImages = await this.getUserProfileImages(userId);
      
      if (profileImages?.profile_picture_url) {
        // Extract file path from URL - format: /storage/v1/object/public/profile-pictures/{userId}/filename
        const url = new URL(profileImages.profile_picture_url);
        const pathParts = url.pathname.split('/');
        const bucketIndex = pathParts.findIndex(part => part === 'profile-pictures');
        if (bucketIndex !== -1 && bucketIndex + 1 < pathParts.length) {
          const filePath = pathParts.slice(bucketIndex + 1).join('/');
          
          // Delete from storage
          await supabase.storage
            .from('profile-pictures')
            .remove([filePath]);
        }
      }

      // Update database
      await supabase
        .from('profile_images')
        .update({ profile_picture_url: null })
        .eq('user_id', userId);
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      throw error;
    }
  }

  /**
   * Delete banner image
   */
  static async deleteBannerImage(userId: string): Promise<void> {
    try {
      const profileImages = await this.getUserProfileImages(userId);
      
      if (profileImages?.banner_image_url) {
        // Extract file path from URL - format: /storage/v1/object/public/profile-banners/{userId}/filename
        const url = new URL(profileImages.banner_image_url);
        const pathParts = url.pathname.split('/');
        const bucketIndex = pathParts.findIndex(part => part === 'profile-banners');
        if (bucketIndex !== -1 && bucketIndex + 1 < pathParts.length) {
          const filePath = pathParts.slice(bucketIndex + 1).join('/');
          
          // Delete from storage
          await supabase.storage
            .from('profile-banners')
            .remove([filePath]);
        }
      }

      // Update database
      await supabase
        .from('profile_images')
        .update({ banner_image_url: null })
        .eq('user_id', userId);
    } catch (error) {
      console.error('Error deleting banner image:', error);
      throw error;
    }
  }
}
